import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm } from '../hooks/useForm';
import { analyzeCase, ApiError, getFormSchema, getTreatmentPackages, updateUserHistory, getFormData, updateFormData, type TreatmentPackage, type FormLanguage } from '../services/api';
import { AnalysisResponse, FormSchemaResponse, FormData } from '../types';
import { useAuth } from '../contexts/AuthContext';
import ProgressHeader from './ProgressHeader';
import ErrorBanner from './ErrorBanner';
import FormSection from './FormSection';
import TesterNameField from './TesterNameField';
import DynamicFormField from './DynamicFormField';
import './DiscoveryCallForm.css';

const EditDiscoveryCallForm: React.FC = () => {
  const { email } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get user_id and appointment_id from URL params
  const userId = searchParams.get('user_id');
  const appointmentId = searchParams.get('appointment_id');

  const {
    formData,
    errors,
    session,
    isSubmitting,
    setIsSubmitting,
    updateField,
    validate,
    validateField,
    setFormData,
  } = useForm();

  // Set tester name to email when component mounts or email changes
  useEffect(() => {
    if (email) {
      updateField('tester_name', email);
    }
  }, [email, updateField]);

  const [currentSection, setCurrentSection] = useState(1);
  const [expandedSections, setExpandedSections] = useState<Set<number>>(
    new Set([1])
  );
  const [completedSections] = useState<Set<number>>(
    new Set()
  );
  const [showErrorBanner, setShowErrorBanner] = useState(false);
  const [testerNameError, setTesterNameError] = useState<string | null>(null);
  const [analysisResult, setAnalysisResult] = useState<AnalysisResponse | null>(
    null
  );
  const [showOutput, setShowOutput] = useState(false);
  const [formSchema, setFormSchema] = useState<FormSchemaResponse | null>(null);
  const [isLoadingSchema, setIsLoadingSchema] = useState(true);
  const [isLoadingFormData, setIsLoadingFormData] = useState(true);
  const [language, setLanguage] = useState<FormLanguage>('english');
  const [showStartSessionModal, setShowStartSessionModal] = useState(false);
  const [packages, setPackages] = useState<TreatmentPackage[]>([]);
  const [isLoadingPackages, setIsLoadingPackages] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState<number | ''>('');
  const [packageError, setPackageError] = useState<string | null>(null);
  const [sessionForm, setSessionForm] = useState({
    treatmentRecommendation: '',
    sexologistType: '',
    reasonNotStarted: '',
    leadStatus: '',
    discountOffered: '',
    triggerOfTreatment: '',
    comments: '',
    lockIn: 'No',
    followUpDate: '',
    followUpTime: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [existingFormId, setExistingFormId] = useState<number | null>(null);
  const [patientInfo, setPatientInfo] = useState<{ id: number; name?: string } | null>(null);

  const formRef = useRef<HTMLFormElement>(null);
  const testerNameRef = useRef<HTMLInputElement>(null);

  // Redirect if no user_id provided
  useEffect(() => {
    if (!userId) {
      alert('User ID is required for editing. Redirecting to form page.');
      navigate('/form');
    }
  }, [userId, navigate]);

  // Fetch form schema when component mounts or language changes
  useEffect(() => {
    const fetchFormSchema = async () => {
      setIsLoadingSchema(true);
      try {
        const response = await getFormSchema(language);
        if (response.statusCode === 200 && response.data) {
          setFormSchema(response);
        }
      } catch (error) {
        console.error('Failed to fetch form schema:', error);
        alert('Failed to load form. Please refresh the page.');
      } finally {
        setIsLoadingSchema(false);
      }
    };

    fetchFormSchema();
  }, [language]);

  // Fetch existing form data on mount
  useEffect(() => {
    const fetchExistingFormData = async () => {
      if (!userId) return;

      setIsLoadingFormData(true);
      try {
        const response = await getFormData(
          parseInt(userId),
          appointmentId ? parseInt(appointmentId) : undefined
        );
        
        if (response && response.data && response.data.form_data) {
          // Store existing form ID for update
          setExistingFormId(response.data.id);
          setPatientInfo({ id: response.data.user_id, name: response.data.form_data.full_name });
          
          // Prefill form with existing data
          const existingData = response.data.form_data;
          setFormData((prev: typeof formData) => ({
            ...prev,
            full_name: existingData.full_name || prev.full_name,
            age: existingData.age ?? prev.age,
            city: existingData.city || prev.city,
            height_cm: existingData.height_cm != null ? Number(existingData.height_cm) : prev.height_cm,
            height_feet: existingData.height_feet != null ? Number(existingData.height_feet) : prev.height_feet,
            height_inches: existingData.height_inches != null ? Number(existingData.height_inches) : prev.height_inches,
            weight_kg: existingData.weight_kg || prev.weight_kg,
            occupation: existingData.occupation || prev.occupation,
            relationship_status: existingData.relationship_status || prev.relationship_status,
            current_sexual_activity: existingData.current_sexual_activity || prev.current_sexual_activity,
            first_consultation: existingData.first_consultation || prev.first_consultation,
            previous_treatments: existingData.previous_treatments || prev.previous_treatments,
            main_issue: existingData.main_issue || prev.main_issue,
            additional_info: existingData.additional_info || prev.additional_info,
            ed_duration: existingData.ed_duration || prev.ed_duration,
            ed_gets_erections: existingData.ed_gets_erections || prev.ed_gets_erections,
            ed_partner_arousal_speed: existingData.ed_partner_arousal_speed || prev.ed_partner_arousal_speed,
            ed_partner_maintenance: existingData.ed_partner_maintenance || prev.ed_partner_maintenance,
            ed_partner_hardness: existingData.ed_partner_hardness || prev.ed_partner_hardness,
            ed_morning_erections: existingData.ed_morning_erections || prev.ed_morning_erections,
            ed_masturbation_imagination: existingData.ed_masturbation_imagination || prev.ed_masturbation_imagination,
            pe_duration: existingData.pe_duration || prev.pe_duration,
            pe_partner_ejaculation_time: existingData.pe_partner_ejaculation_time || prev.pe_partner_ejaculation_time,
            pe_partner_penile_sensitivity: existingData.pe_partner_penile_sensitivity || prev.pe_partner_penile_sensitivity,
            pe_partner_masturbation_control: existingData.pe_partner_masturbation_control || prev.pe_partner_masturbation_control,
            masturbation_method: existingData.masturbation_method || prev.masturbation_method,
            masturbation_grip: existingData.masturbation_grip || prev.masturbation_grip,
            masturbation_frequency: existingData.masturbation_frequency || prev.masturbation_frequency,
            porn_frequency: existingData.porn_frequency || prev.porn_frequency,
            performance_anxiety: existingData.performance_anxiety || prev.performance_anxiety,
            multiple_partners: existingData.multiple_partners || prev.multiple_partners,
            issues_all_partners: existingData.issues_all_partners || prev.issues_all_partners,
            partner_response: existingData.partner_response || prev.partner_response,
            medical_conditions: existingData.medical_conditions || prev.medical_conditions,
            medical_conditions_other: existingData.medical_conditions_other || prev.medical_conditions_other,
            current_medications: existingData.current_medications || prev.current_medications,
            current_medications_other: existingData.current_medications_other || prev.current_medications_other,
            spinal_genital_surgery: existingData.spinal_genital_surgery || prev.spinal_genital_surgery,
            emergency_red_flags: existingData.emergency_red_flags || prev.emergency_red_flags,
            alcohol_frequency: existingData.alcohol_frequency || prev.alcohol_frequency,
            smoking_frequency: existingData.smoking_frequency || prev.smoking_frequency,
            substance_consumption: existingData.substance_consumption || prev.substance_consumption,
            stress_level: existingData.stress_level || prev.stress_level,
            sleep_quality: existingData.sleep_quality || prev.sleep_quality,
            physical_activity: existingData.physical_activity || prev.physical_activity,
          }));

          // Prefill session form with existing popup data if available
          if (response.data.treatment_package) {
            setSelectedPackageId(response.data.treatment_package);
          }
          setSessionForm({
            treatmentRecommendation: response.data.treatment_recommendation || '',
            sexologistType: response.data.type_of_sexologist_recommended || '',
            reasonNotStarted: response.data.reason_of_treatment_not_started || '',
            leadStatus: response.data.status_of_lead || '',
            discountOffered: response.data.discount_offered || '',
            triggerOfTreatment: response.data.trigger_of_treatment || '',
            comments: response.data.comments || '',
            lockIn: response.data.lock_in || 'No',
            followUpDate: response.data.follow_up_date || '',
            followUpTime: response.data.follow_up_time || '',
          });

          console.log('Form prefilled with existing data. Form ID:', response.data.id);
        } else {
          // No existing data found - redirect to create new form
          console.log('No existing form data found for this user. Redirecting to new form.');
          navigate('/form');
        }
      } catch (error) {
        // This shouldn't happen now since getFormData doesn't throw
        console.log('Could not load existing form data. Redirecting to new form.');
        navigate('/form');
      } finally {
        setIsLoadingFormData(false);
      }
    };

    fetchExistingFormData();
  }, [userId, appointmentId, setFormData, navigate]);

  const fetchPackages = useCallback(async () => {
    setIsLoadingPackages(true);
    setPackageError(null);
    try {
      const response = await getTreatmentPackages();
      setPackages(response.data || []);
    } catch (error) {
      console.error('Failed to fetch treatment packages:', error);
      setPackageError('Failed to load treatment packages');
    } finally {
      setIsLoadingPackages(false);
    }
  }, []);

  useEffect(() => {
    if (showStartSessionModal && packages.length === 0 && !isLoadingPackages) {
      fetchPackages();
    }
  }, [showStartSessionModal, packages.length, isLoadingPackages, fetchPackages]);

  // Update current section when user interacts with fields
  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      const section = target.closest('[data-section]') as HTMLElement;
      if (section) {
        const sectionNum = parseInt(section.dataset.section || '0');
        if (sectionNum && sectionNum !== currentSection) {
          setCurrentSection(sectionNum);
          setExpandedSections((prev) => new Set([...prev, sectionNum]));
        }
      }
    };

    document.addEventListener('focus', handleFocus, true);
    return () => document.removeEventListener('focus', handleFocus, true);
  }, [currentSection]);

  const formatPackageLabel = useCallback((pkg: TreatmentPackage) => {
    const counsellor = pkg.counsellor_consultations ?? 0;
    const sexologist = pkg.sexologist_consultations ?? 0;
    const therapist = pkg.therapist_consultations ?? 0;
    return `${pkg.name} (₹${pkg.price}) - C(${counsellor}) - S(${sexologist}) - T(${therapist})`;
  }, []);

  const updateSessionField = useCallback((field: keyof typeof sessionForm, value: string) => {
    setSessionForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleStartSessionSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      
      if (!userId) {
        alert('User ID is required');
        return;
      }

      // Build JSON body with all fields
      const payload = {
        user_id: parseInt(userId),
        appointment_id: appointmentId ? parseInt(appointmentId) : undefined,
        treatment_package: selectedPackageId != null && (typeof selectedPackageId !== 'string' || selectedPackageId !== '') ? (typeof selectedPackageId === 'number' ? selectedPackageId : parseInt(String(selectedPackageId), 10)) : null,
        treatment_recommendation: sessionForm.treatmentRecommendation || null,
        type_of_sexologist_recommended: sessionForm.sexologistType || null,
        reason_of_treatment_not_started: sessionForm.reasonNotStarted || null,
        status_of_lead: sessionForm.leadStatus || null,
        discount_offered: sessionForm.discountOffered || null,
        trigger_of_treatment: sessionForm.triggerOfTreatment || null,
        comments: sessionForm.comments || null,
        lock_in: sessionForm.lockIn || null,
        follow_up_date: sessionForm.followUpDate || null,
        follow_up_time: sessionForm.followUpTime || null,
      };

      // Print formatted JSON body
      console.log('Start New Session - JSON Body:');
      console.log(JSON.stringify(payload, null, 2));
      
      // Also print in a readable format
      console.log('\nStart New Session - Field Values:');
      Object.entries(payload).forEach(([field, value]) => {
        console.log(`  ${field}: ${value === null || value === undefined ? '(empty)' : value}`);
      });

      // Call API to update user history
      try {
        const response = await updateUserHistory(payload);
        console.log('User history updated successfully:', response);
        alert('User history updated successfully!');
        setShowStartSessionModal(false);
        
        // Reset form after successful submission
        setSessionForm({
          treatmentRecommendation: '',
          sexologistType: '',
          reasonNotStarted: '',
          leadStatus: '',
          discountOffered: '',
          triggerOfTreatment: '',
          comments: '',
          lockIn: 'No',
          followUpDate: '',
          followUpTime: '',
        });
        setSelectedPackageId('');
        setUploadFile(null);
      } catch (error) {
        console.error('Failed to update user history:', error);
        if (error instanceof ApiError) {
          alert(`Failed to update user history: ${error.message}`);
        } else {
          alert('Failed to update user history. Please try again.');
        }
      }
    },
    [sessionForm, selectedPackageId, userId, appointmentId, uploadFile]
  );

  // Calculate section states for indicators
  const totalSections = formSchema?.data.total_sections || 5;
  const sectionStates = React.useMemo(() => {
    const states: { [key: number]: 'active' | 'completed' | 'upcoming' } = {};
    for (let i = 1; i <= totalSections; i++) {
      if (completedSections.has(i)) {
        states[i] = 'completed';
      } else if (i === currentSection) {
        states[i] = 'active';
      } else {
        states[i] = 'upcoming';
      }
    }
    return states;
  }, [currentSection, completedSections, totalSections]);

  const toggleSection = useCallback((sectionNum: number) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionNum)) {
        newSet.delete(sectionNum);
      } else {
        newSet.add(sectionNum);
        setCurrentSection(sectionNum);
      }
      return newSet;
    });
  }, []);

  const handleSectionClick = useCallback((sectionNum: number) => {
    setCurrentSection(sectionNum);
    setExpandedSections((prev) => new Set([...prev, sectionNum]));
    // Scroll to section
    setTimeout(() => {
      const section = document.querySelector(`[data-section="${sectionNum}"]`);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setShowErrorBanner(false);
      setTesterNameError(null);

      // Validate tester name first
      const testerError = validateTesterName(formData.tester_name);
      if (testerError) {
        setTesterNameError(testerError);
        setShowErrorBanner(true);
        setTimeout(() => {
          testerNameRef.current?.focus();
          testerNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
        return;
      }

      // Validate entire form
      if (!validate()) {
        setShowErrorBanner(true);
        // Scroll to first error
        const firstError = errors[0];
        if (firstError) {
          const field = document.querySelector(`[name="${firstError.field}"]`);
          if (field) {
            field.scrollIntoView({ behavior: 'smooth', block: 'center' });
            (field as HTMLElement).focus();
          }
        }
        return;
      }

      if (!existingFormId) {
        alert('No existing form ID found. Cannot update.');
        return;
      }

      setIsSubmitting(true);
      try {
        const result = await analyzeCase({
          ...formData,
          session_id: session.sessionId,
          completion_time_seconds: session.getCompletionTime(),
          patient_id: userId ? parseInt(userId) : undefined,
        } as any);
        setAnalysisResult(result);
        setShowOutput(true);

        // Update existing form response to database
        try {
          // Extract primary and secondary root causes
          const primaryRootCause = result.root_causes && result.root_causes.length > 0
            ? result.root_causes[0].simple_term
            : undefined;
          
          const secondaryRootCause = result.root_causes && result.root_causes.length > 1
            ? result.root_causes[1].simple_term
            : undefined;

          const updateResponse = await updateFormData({
            id: existingFormId,
            form_data: {
              ...formData,
              session_id: session.sessionId,
              completion_time_seconds: session.getCompletionTime(),
              patient_id: userId ? parseInt(userId) : undefined,
            },
            analysis_result: result,
            primary_root_cause: primaryRootCause,
            secondary_root_cause: secondaryRootCause,
            root_cause_explanation: result.root_cause_explanation || undefined,
            other_information: formData.additional_info || undefined,
          });
          
          console.log('Form response updated successfully:', updateResponse);
        } catch (saveError) {
          // Log error but don't block user experience
          console.error('Failed to update form response:', saveError);
          if (saveError instanceof ApiError) {
            console.error('Update error details:', saveError.message);
          }
        }
      } catch (error: any) {
        // Handle ApiError with better error messages
        if (error instanceof ApiError) {
          let errorMessage = error.message;
          
          // Provide user-friendly messages for specific error types
          if (error.isCorsError && error.isCorsError()) {
            errorMessage = 'CORS Error: The server is not configured to accept requests from this origin.';
          } else if (error.isTimeout()) {
            errorMessage = 'Request timed out. Please try again.';
          } else if (error.isNetworkError()) {
            errorMessage = 'Network error. Please check your internet connection.';
          } else if (error.isServerError()) {
            errorMessage = 'Server error. Please try again later.';
          } else if (error.isClientError()) {
            errorMessage = error.message || 'Invalid request. Please check your form data.';
          }
          
          alert(`Error: ${errorMessage}`);
        } else if (error instanceof Error) {
          alert(`Error: ${error.message || 'Failed to analyze case. Please try again.'}`);
        } else {
          alert('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [formData, validate, errors, session, setIsSubmitting, existingFormId, userId]
  );

  const handleFieldClick = useCallback((fieldName: string) => {
    const field = document.querySelector(`[name="${fieldName}"]`);
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (field as HTMLElement).focus();
    }
  }, []);

  // Validate tester name field
  const validateTesterName = useCallback((value: string): string | null => {
    if (!value || value.trim() === '') {
      return 'Tester name is not filled';
    }
    if (value.trim().length < 2) {
      return 'Tester name must be at least 2 characters';
    }
    if (value.length > 100) {
      return 'Tester name must be less than 100 characters';
    }
    return null;
  }, []);

  // Convert errors array to object for easier access
  const errorMap = React.useMemo(() => {
    const map: { [key: string]: string } = {};
    errors.forEach((err) => {
      map[err.field] = err.message;
    });
    return map;
  }, [errors]);

  // Show loading state while data is being fetched
  if (isLoadingSchema || isLoadingFormData) {
    return (
      <div className="discovery-call-form">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p>Loading form data...</p>
        </div>
      </div>
    );
  }

  // Show error if schema failed to load
  if (!formSchema || !formSchema.data) {
    return (
      <div className="discovery-call-form">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <p style={{ color: '#ff0000' }}>Failed to load form. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="discovery-call-form">
      <ProgressHeader
        currentSection={currentSection}
        totalSections={totalSections}
        onSectionClick={handleSectionClick}
        sectionStates={sectionStates}
      />

      {showErrorBanner && (
        <ErrorBanner
          errors={errors}
          testerNameError={testerNameError || undefined}
          onClose={() => setShowErrorBanner(false)}
          onFieldClick={handleFieldClick}
        />
      )}

      <main className="main-container">
        <div className="form-panel">
          {/* Edit mode header */}
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <div>
              <strong style={{ color: '#856404' }}>📝 Edit Mode</strong>
              <p style={{ margin: '4px 0 0', color: '#856404', fontSize: '14px' }}>
                Editing form for User ID: {userId}
                {patientInfo?.name && ` (${patientInfo.name})`}
                {appointmentId && ` | Appointment ID: ${appointmentId}`}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate('/form')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              ← Back to New Form
            </button>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} noValidate>
            <TesterNameField
              value={email || formData.tester_name || ''}
              onChange={(v) => updateField('tester_name', v)}
              onBlur={() => {
                const testerName = email || formData.tester_name || '';
                const error = validateTesterName(testerName);
                if (error) setTesterNameError(error);
                else setTesterNameError(null);
              }}
              error={testerNameError || errorMap.tester_name}
              disabled={true}
            />

            {/* Language Toggle */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
              padding: '12px 16px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
            }}>
              <span style={{ fontWeight: 600, color: '#495057' }}>Language:</span>
              <div style={{
                display: 'flex',
                gap: '8px',
              }}>
                <button
                  type="button"
                  onClick={() => setLanguage('english')}
                  disabled={isLoadingSchema}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: language === 'english' ? '2px solid #007bff' : '1px solid #ced4da',
                    backgroundColor: language === 'english' ? '#007bff' : '#fff',
                    color: language === 'english' ? '#fff' : '#495057',
                    fontWeight: language === 'english' ? 600 : 400,
                    cursor: isLoadingSchema ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  🇬🇧 English
                </button>
                <button
                  type="button"
                  onClick={() => setLanguage('hindi')}
                  disabled={isLoadingSchema}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '6px',
                    border: language === 'hindi' ? '2px solid #007bff' : '1px solid #ced4da',
                    backgroundColor: language === 'hindi' ? '#007bff' : '#fff',
                    color: language === 'hindi' ? '#fff' : '#495057',
                    fontWeight: language === 'hindi' ? 600 : 400,
                    cursor: isLoadingSchema ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  🇮🇳 हिंदी
                </button>
              </div>
              {isLoadingSchema && (
                <span style={{ fontSize: '14px', color: '#6c757d' }}>Loading...</span>
              )}
            </div>

            {/* Render all sections directly since form is already loaded */}
            {formSchema.data.sections.map((section) => (
              <FormSection
                key={section.section_number}
                sectionNumber={section.section_number}
                title={`Section ${section.section_number}: ${section.section_name}`}
                isExpanded={expandedSections.has(section.section_number)}
                isComplete={completedSections.has(section.section_number)}
                onToggle={() => toggleSection(section.section_number)}
              >
                {section.questions.map((question) => {
                  // Get field value safely with type assertion
                  const fieldValue = (formData as Record<string, any>)[question.field_name];
                  
                  return (
                    <DynamicFormField
                      key={question.id}
                      question={question}
                      value={fieldValue}
                      onChange={(value) => {
                        // Handle height field specially
                        if (question.type === 'height' && typeof value === 'object' && value !== null) {
                          const heightData = value as { cm?: number; feet?: number; inches?: number };
                          if (heightData.cm !== undefined) {
                            updateField('height_cm', heightData.cm);
                            updateField('height_feet', undefined);
                            updateField('height_inches', undefined);
                          } else {
                            updateField('height_cm', undefined);
                            updateField('height_feet', heightData.feet);
                            updateField('height_inches', heightData.inches);
                          }
                        } else {
                          // Use type assertion for dynamic field names
                          updateField(question.field_name as keyof FormData, value);
                        }
                      }}
                      onValidate={(fieldName, val, isRequired) => {
                        if (validateField) {
                          return validateField(fieldName as keyof FormData, val, isRequired);
                        }
                        return null;
                      }}
                      error={errorMap[question.field_name]}
                      formData={formData}
                      disabled={false}
                      onNavigateToSection={handleSectionClick}
                    />
                  );
                })}
              </FormSection>
            ))}

            <div style={{ marginTop: '32px', padding: '0 16px' }}>
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Analyzing...' : 'Update & Analyze'}
              </button>
            </div>
          </form>
        </div>

        {/* Output Panel */}
        {showOutput && analysisResult && (
          <div className="output-panel">
            <div className="analysis-output">
              <div className="analysis-header">
                <h2 className="analysis-title">Analysis Result</h2>
                <span className="analysis-badge">Updated</span>
              </div>
              
              {/* Primary Root Cause */}
              {analysisResult.root_causes && analysisResult.root_causes.length > 0 && (
                <div className="root-cause-section primary">
                  <h3>Primary Root Cause</h3>
                  <div className="root-cause-content">
                    <span className="root-cause-term">{analysisResult.root_causes[0].simple_term}</span>
                    <span className="root-cause-technical">({analysisResult.root_causes[0].technical_term ?? analysisResult.root_causes[0].simple_term})</span>
                  </div>
                </div>
              )}
              
              {/* Secondary Root Cause */}
              {analysisResult.root_causes && analysisResult.root_causes.length > 1 && (
                <div className="root-cause-section secondary">
                  <h3>Secondary Root Cause</h3>
                  <div className="root-cause-content">
                    <span className="root-cause-term">{analysisResult.root_causes[1].simple_term}</span>
                    <span className="root-cause-technical">({analysisResult.root_causes[1].technical_term ?? analysisResult.root_causes[1].simple_term})</span>
                  </div>
                </div>
              )}
              
              <div className="separator" />
              
              {/* Root Cause Explanation */}
              {analysisResult.root_cause_explanation && (
                <div className="explanation-section">
                  <h3>Root Cause Explanation</h3>
                  <p>{analysisResult.root_cause_explanation}</p>
                </div>
              )}
              
              {/* Treatment Explanation */}
              {analysisResult.treatment_explanation && (
                <div className="explanation-section">
                  <h3>Treatment Explanation</h3>
                  <p>{analysisResult.treatment_explanation}</p>
                </div>
              )}

              {/* Start New Session Button */}
              <div style={{ marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={() => setShowStartSessionModal(true)}
                  className="start-session-button"
                >
                  Start New Session
                </button>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Start New Session Modal */}
      {showStartSessionModal && (
        <div className="modal-backdrop" onClick={() => setShowStartSessionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Start New Session</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setShowStartSessionModal(false)}
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleStartSessionSubmit}>
              <div className="modal-grid">
                <label className="modal-field">
                  <span>Treatment Package</span>
                  {isLoadingPackages ? (
                    <select disabled>
                      <option>Loading packages...</option>
                    </select>
                  ) : packageError ? (
                    <select disabled>
                      <option>{packageError}</option>
                    </select>
                  ) : (
                    <select
                      value={selectedPackageId}
                      onChange={(e) => setSelectedPackageId(e.target.value === '' ? '' : Number(e.target.value))}
                    >
                      <option value="">Same as User History</option>
                      {packages.map((pkg) => (
                        <option key={pkg.id} value={pkg.id}>
                          {formatPackageLabel(pkg)}
                        </option>
                      ))}
                    </select>
                  )}
                </label>

                <label className="modal-field">
                  <span>Treatment Recommendation</span>
                  <input
                    type="text"
                    value={sessionForm.treatmentRecommendation}
                    onChange={(e) => updateSessionField('treatmentRecommendation', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>

                <label className="modal-field">
                  <span>Type of Sexologist Recommended</span>
                  <select
                    value={sessionForm.sexologistType}
                    onChange={(e) => updateSessionField('sexologistType', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Psychiatrist">Psychiatrist</option>
                    <option value="Andrologist">Andrologist</option>
                    <option value="Gynaecologist">Gynaecologist</option>
                    <option value="Only Therapist">Only Therapist</option>
                    <option value="Venereologist">Venereologist</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Reason of Treatment Not Started</span>
                  <select
                    value={sessionForm.reasonNotStarted}
                    onChange={(e) => updateSessionField('reasonNotStarted', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Payment done">Payment done</option>
                    <option value="Not sure of the reason">Not sure of the reason</option>
                    <option value="Treatment started in DC">Treatment started in DC</option>
                    <option value="Active pain / bleeding case / creep">Active pain / bleeding case / creep</option>
                    <option value="Treatment wanted in 99">Treatment wanted in 99</option>
                    <option value="Under age">Under age</option>
                    <option value="Low cibil score">Low cibil score</option>
                    <option value="Low self confidence because of low performance">Low self confidence because of low performance</option>
                    <option value="User found treatment costly">User found treatment costly</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Status of Lead</span>
                  <select
                    value={sessionForm.leadStatus}
                    onChange={(e) => updateSessionField('leadStatus', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Promise to Pay">Promise to Pay</option>
                    <option value="Feedback">Feedback</option>
                    <option value="Not Interested">Not Interested</option>
                    <option value="Treatment Started in DC">Treatment Started in DC</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Discount Offered</span>
                  <input
                    type="text"
                    value={sessionForm.discountOffered}
                    onChange={(e) => updateSessionField('discountOffered', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>

                <label className="modal-field">
                  <span>Add a Trigger of Treatment</span>
                  <select
                    value={sessionForm.triggerOfTreatment}
                    onChange={(e) => updateSessionField('triggerOfTreatment', e.target.value)}
                  >
                    <option value="">Same as User History</option>
                    <option value="Low self confidence because of low performance">Low self confidence because of low performance</option>
                    <option value="Performance anxiety">Performance anxiety</option>
                    <option value="Partner feedback">Partner feedback</option>
                    <option value="Relationship issues">Relationship issues</option>
                    <option value="Health concerns">Health concerns</option>
                    <option value="Desire for improvement">Desire for improvement</option>
                    <option value="Medical recommendation">Medical recommendation</option>
                    <option value="Personal dissatisfaction">Personal dissatisfaction</option>
                  </select>
                </label>

                <label className="modal-field modal-field-full">
                  <span>Comments</span>
                  <textarea
                    value={sessionForm.comments}
                    onChange={(e) => updateSessionField('comments', e.target.value)}
                    placeholder="Same as User History"
                    rows={3}
                  />
                </label>

                <label className="modal-field">
                  <span>Upload File</span>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                </label>

                <label className="modal-field">
                  <span>Lock-In</span>
                  <select
                    value={sessionForm.lockIn}
                    onChange={(e) => updateSessionField('lockIn', e.target.value)}
                  >
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </label>

                <label className="modal-field">
                  <span>Follow Up Date</span>
                  <input
                    type="date"
                    value={sessionForm.followUpDate}
                    onChange={(e) => updateSessionField('followUpDate', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>

                <label className="modal-field">
                  <span>Follow Up Time</span>
                  <input
                    type="time"
                    value={sessionForm.followUpTime}
                    onChange={(e) => updateSessionField('followUpTime', e.target.value)}
                    placeholder="Same as User History"
                  />
                </label>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowStartSessionModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => window.location.reload()}
                >
                  Skip
                </button>
                <button type="submit" className="primary-button">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDiscoveryCallForm;

